import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Plus,
  AlertTriangle,
  TrendingUp,
  Search,
  BarChart3,
  Edit,
  Trash2,
  Menu
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { quickEntryService } from '@/services/quickEntryService';
import { IndustryRiskSearch } from './IndustryRiskSearch';
import { RiskAnalyticsDashboard } from './RiskAnalyticsDashboard';
import { EnhancedRiskModal } from './EnhancedRiskModal';

interface RiskAssessment {
  id: string;
  user_id: string;
  risk_name: string;
  risk_description: string;
  risk_category: string;
  likelihood: number;
  impact: number;
  risk_score: number;
  mitigation_strategy: string;
  responsible_person: string;
  review_date: string;
  status: string;
  created_at: string;
  updated_at: string;
  source?: string;
  industry_category?: string;
  industry_subcategory?: string;
  industry_tags?: string[];
  notes?: string;
}

interface IndustryRisk {
  id: string;
  risk_name: string;
  risk_description: string;
  mitigation_strategy: string;
  risk_category: string;
  industry_category: string;
  industry_subcategory: string;
  industry_tags: string[];
  likelihood: number;
  impact: number;
}

export const RiskManagementMain = () => {
  const { toast } = useToast();
  const [risks, setRisks] = useState<RiskAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState<RiskAssessment | undefined>();
  const [selectedIndustryRisk, setSelectedIndustryRisk] = useState<IndustryRisk | undefined>();
  const [activeTab, setActiveTab] = useState('overview');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [riskToDelete, setRiskToDelete] = useState<RiskAssessment | null>(null);

  useEffect(() => {
    fetchRisks();
  }, []);

  const fetchRisks = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('risk_assessments')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate risk scores for any risks that don't have them
      const processedRisks = (data || []).map(risk => ({
        ...risk,
        risk_score: risk.likelihood * risk.impact
      }));

      setRisks(processedRisks);
    } catch (error: any) {
      console.error('Error fetching risks:', error);
      toast({
        title: "Error",
        description: "Failed to fetch risk assessments",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddRisk = () => {
    setSelectedRisk(undefined);
    setSelectedIndustryRisk(undefined);
    setIsModalOpen(true);
  };

  const handleEditRisk = (risk: RiskAssessment) => {
    setSelectedRisk(risk);
    setSelectedIndustryRisk(undefined);
    setIsModalOpen(true);
  };

  const handleSelectIndustryRisk = (industryRisk: IndustryRisk) => {
    setSelectedIndustryRisk(industryRisk);
    setSelectedRisk(undefined);
    setIsModalOpen(true);
  };

  const handleDeleteRisk = (risk: RiskAssessment) => {
    setRiskToDelete(risk);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!riskToDelete) return;

    try {
      const { error } = await supabase
        .from('risk_assessments')
        .delete()
        .eq('id', riskToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Risk assessment deleted successfully",
      });

      // Refresh the risks list
      await fetchRisks();

      // Close dialog
      setIsDeleteDialogOpen(false);
      setRiskToDelete(null);
    } catch (error: any) {
      console.error('Error deleting risk:', error);
      toast({
        title: "Error",
        description: "Failed to delete risk assessment",
        variant: "destructive",
      });
      // Close dialog on error too
      setIsDeleteDialogOpen(false);
      setRiskToDelete(null);
    }
  };

  const handleSaveRisk = async (riskData: any) => {
    try {
      console.log('handleSaveRisk called with:', { riskData, selectedRisk });

      let result;

      if (selectedRisk) {
        // Update existing risk
        const updatedRisk = {
          ...riskData,
          updated_at: new Date().toISOString()
        };

        console.log('Updating risk with data:', updatedRisk);

        const { data, error } = await supabase
          .from('risk_assessments')
          .update(updatedRisk)
          .eq('id', selectedRisk.id)
          .select()
          .single();

        if (error) throw error;
        result = { success: true, data };
      } else {
        // Create new risk using quickEntryService
        console.log('Creating new risk with data:', riskData);
        result = await quickEntryService.createRiskAssessment(riskData);
      }

      console.log('Supabase operation result:', result);

      if (result.success) {
        toast({
          title: "Success",
          description: selectedRisk ? "Risk assessment updated successfully" : "Risk assessment created successfully",
        });

        setIsModalOpen(false);
        await fetchRisks();
      } else {
        throw new Error(result.error || 'Failed to save risk assessment');
      }
    } catch (error: any) {
      console.error('Error saving risk:', error);
      toast({
        title: "Error",
        description: `Failed to save risk assessment: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleAddIndustryRisk = async (industryRisk: IndustryRisk) => {
    try {
      console.log('Adding industry risk to Supabase:', industryRisk);

      // Convert industry risk to user risk assessment format
      const riskData = {
        risk_name: industryRisk.risk_name,
        risk_description: industryRisk.risk_description,
        risk_category: industryRisk.risk_category,
        likelihood: industryRisk.likelihood,
        impact: industryRisk.impact,
        mitigation_strategy: industryRisk.mitigation_strategy,
        responsible_person: '',
        review_date: '',
        status: 'active',
        notes: `Source: Industry Database\nCategory: ${industryRisk.industry_category}\nSubcategory: ${industryRisk.industry_subcategory}\nTags: ${industryRisk.industry_tags?.join(', ')}`
      };

      console.log('User risk data to save:', riskData);

      // Save to Supabase using quickEntryService
      const result = await quickEntryService.createRiskAssessment(riskData);

      console.log('Supabase add result:', result);

      if (result.success) {
        toast({
          title: "Success",
          description: "Industry risk added to your assessments successfully",
        });

        setIsModalOpen(false);
        await fetchRisks();
      } else {
        throw new Error(result.error || 'Failed to add industry risk');
      }
    } catch (error: any) {
      console.error('Error adding industry risk:', error);
      toast({
        title: "Error",
        description: `Failed to add industry risk to your assessments: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const getRiskScoreColor = (score: number) => {
    if (score >= 20) return 'bg-red-500';
    if (score >= 15) return 'bg-orange-500';
    if (score >= 10) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getRiskScoreLabel = (score: number) => {
    if (score >= 20) return 'Critical';
    if (score >= 15) return 'High';
    if (score >= 10) return 'Medium';
    return 'Low';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-800';
      case 'mitigated': return 'bg-yellow-100 text-yellow-800';
      case 'closed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const highRiskCount = risks.filter(r => r.risk_score >= 15).length;
  const averageRiskScore = risks.length > 0
    ? risks.reduce((sum, risk) => sum + risk.risk_score, 0) / risks.length
    : 0;

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  const tabs = [
    { value: 'overview', label: 'Risk Overview', icon: BarChart3 },
    { value: 'search', label: 'Industry Risk Database', icon: Search },
    { value: 'analytics', label: 'Analytics Dashboard', icon: TrendingUp },
  ];

  const activeTabLabel = tabs.find(t => t.value === activeTab)?.label || 'Risk Overview';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Risk Management</h1>
          <p className="text-muted-foreground">
            Identify, assess, and manage organizational risks effectively
          </p>
        </div>
        <Button onClick={handleAddRisk} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Risk Assessment
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Risks</p>
                <p className="text-2xl font-bold">{risks.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">High Risk</p>
                <p className="text-2xl font-bold text-red-600">{highRiskCount}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Risk Score</p>
                <p className="text-2xl font-bold">{averageRiskScore.toFixed(1)}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Risks</p>
                <p className="text-2xl font-bold">{risks.filter(r => r.status === 'active').length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Desktop Tabs List */}
        <TabsList className="hidden md:grid w-full grid-cols-3">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2">
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Mobile Tabs Dropdown */}
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full flex justify-start items-center gap-3 px-4 py-6">
                <Menu className="h-5 w-5 text-gray-500" />
                <span className="flex items-center gap-2 border-l pl-3">
                  {React.createElement(tabs.find(t => t.value === activeTab)?.icon || BarChart3, { className: "h-5 w-5" })}
                  <span className="font-semibold">{activeTabLabel}</span>
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64" align="start">
              {tabs.map((tab) => (
                <DropdownMenuItem
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className="flex items-center gap-2 py-3"
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Your Risk Assessments</CardTitle>
            </CardHeader>
            <CardContent>
              {risks.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Risk Assessments</h3>
                  <p className="text-muted-foreground mb-4">
                    Start by adding your first risk assessment or browse the industry risk database
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={handleAddRisk}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Risk Assessment
                    </Button>
                    <Button variant="outline" onClick={() => setActiveTab('search')}>
                      <Search className="h-4 w-4 mr-2" />
                      Browse Industry Risks
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {risks.map((risk) => (
                    <Card key={risk.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold">{risk.risk_name}</h3>
                              <Badge className={getRiskScoreColor(risk.risk_score)}>
                                {getRiskScoreLabel(risk.risk_score)}
                              </Badge>
                              <Badge className={getStatusColor(risk.status)}>
                                {risk.status}
                              </Badge>
                              {risk.notes && risk.notes.includes('Source: Industry Database') && (
                                <Badge variant="outline" className="text-xs">
                                  From Industry DB
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {risk.risk_description}
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="font-medium">Category:</span> {risk.risk_category}
                              </div>
                              <div>
                                <span className="font-medium">Likelihood:</span> {risk.likelihood}/5
                              </div>
                              <div>
                                <span className="font-medium">Impact:</span> {risk.impact}/5
                              </div>
                              <div>
                                <span className="font-medium">Score:</span> {risk.risk_score}
                              </div>
                            </div>
                            {risk.responsible_person && (
                              <p className="text-sm text-muted-foreground mt-2">
                                <span className="font-medium">Responsible:</span> {risk.responsible_person}
                              </p>
                            )}
                            {risk.review_date && (
                              <p className="text-sm text-muted-foreground mt-1">
                                <span className="font-medium">Review Date:</span> {new Date(risk.review_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditRisk(risk)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteRisk(risk)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="search">
          <IndustryRiskSearch onSelectRisk={handleSelectIndustryRisk} />
        </TabsContent>

        <TabsContent value="analytics">
          <RiskAnalyticsDashboard />
        </TabsContent>
      </Tabs>

      <EnhancedRiskModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={selectedIndustryRisk ? handleAddIndustryRisk : handleSaveRisk}
        risk={selectedRisk}
        industryRisk={selectedIndustryRisk}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{riskToDelete?.risk_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setRiskToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
